import React, { useEffect, useRef } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth, canAccess } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CommandPalette, { useCommandPalette } from "@/pages/CommandPalette";
import MenuPublishBar from "@/components/MenuPublishBar";
import { hasPermission } from "@/lib/permissions";
import { ConnectivityBanner } from "@/components/ConnectivityBanner";
import { BarChart3, Coffee, MapPin, Users, Settings, Plug, UserCog, LogOut, LayoutGrid, KeyRound, Layers, ScrollText, ToggleLeft, Eye, Activity, Server, TrendingUp, Command, LayoutTemplate, Sparkles, History, CalendarClock } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard live", Icon: Activity, end: true, perm: "admin.dashboard" },
  { to: "/admin/reports", label: "Report", Icon: BarChart3, perm: "admin.reports" },
  { to: "/admin/activity", label: "Cronologia", Icon: ScrollText, perm: "admin.activity" },
  { to: "/admin/menu", label: "Menu (contenuti)", Icon: Coffee, perm: "admin.menu" },
  { to: "/admin/menu/history", label: "Versioni & Rollback", Icon: History, perm: "admin.menu_history" },
  { to: "/admin/menu/templates", label: "Template Modificatori", Icon: LayoutTemplate, perm: "admin.templates" },
  { to: "/admin/menu/suggestions", label: "Suggerimenti di vendita", Icon: Sparkles, perm: "admin.suggestions" },
  { to: "/admin/availability", label: "Disponibilità Rapida", Icon: ToggleLeft, perm: "admin.availability" },
  { to: "/admin/menus", label: "Menu multipli", Icon: Layers, perm: "admin.menus" },
  { to: "/admin/customers", label: "Clienti CRM", Icon: Users, perm: "admin.crm" },
  { to: "/admin/crm/lists", label: "Liste CRM", Icon: Layers, perm: "admin.crm" },
  { to: "/admin/reservations", label: "Agenda Prenotazioni", Icon: CalendarClock, perm: "admin.reservations" },
  { to: "/admin/rooms", label: "Sale", Icon: LayoutGrid, perm: "admin.settings" },
  { to: "/admin/stations", label: "Postazioni & QR", Icon: MapPin, perm: "admin.qr" },
  { to: "/admin/users", label: "Utenti", Icon: UserCog, perm: "admin.users" },
  { to: "/admin/audit", label: "Audit log", Icon: TrendingUp, perm: "admin.audit" },
  { to: "/admin/system", label: "Stato sistema", Icon: Server, perm: "admin.system" },
  { to: "/admin/settings", label: "Impostazioni", Icon: Settings, perm: "admin.settings" },
  { to: "/admin/integrations", label: "Integrazioni", Icon: Plug, perm: "admin.integrations" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const sinceRef = useRef(new Date().toISOString());
  const cmdk = useCommandPalette();

  // Global keyboard shortcuts (in addition to Cmd/Ctrl+K handled by useCommandPalette)
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const inField = /input|textarea|select/i.test(e.target.tagName) || e.target.isContentEditable;
      // '/' focuses search on availability page
      if (!inField && e.key === "/") {
        const el = document.querySelector('[data-testid="avail-search"], [data-testid="menu-search"], [data-testid="activity-search"]');
        if (el) { e.preventDefault(); el.focus(); }
      }
      // Cmd/Ctrl+F → focus first search input on the page
      if (mod && (e.key === "f" || e.key === "F") && !inField) {
        const el = document.querySelector('[data-testid$="search"], [data-testid$="-search"]');
        if (el) { e.preventDefault(); el.focus(); }
      }
      // Cmd/Ctrl+N → new product/category on menu page
      if (mod && (e.key === "n" || e.key === "N") && !inField) {
        if (window.location.pathname.startsWith("/admin/menu")) {
          e.preventDefault();
          const btn = document.querySelector('[data-testid="new-prod-btn"], [data-testid="new-cat-btn"]');
          if (btn) btn.click();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Global poller: notify staff on scheduler auto-flips (non-invasive)
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const check = async () => {
      try {
        const r = await api.get(`/admin/scheduler/pending-events?since=${encodeURIComponent(sinceRef.current)}`);
        if (!mounted) return;
        (r.data.events || []).forEach((e) => {
          const label = e.entity_type === "product" ? "Prodotto" : "Categoria";
          const action = e.to_state ? (e.entity_type === "product" ? "ora disponibile" : "ora attiva") : "ora nascosto";
          toast.info(`${label} "${e.entity_name}" ${action} (programmazione automatica)`, {
            duration: 4000, id: `sched-${e.id}`,
          });
        });
        sinceRef.current = r.data.server_time || sinceRef.current;
      } catch { /* silent */ }
    };
    const t = setInterval(check, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, [user]);
  return (
    <div className="min-h-screen flex">
      <aside className="w-64 border-r border-sand-300 bg-white p-5 hidden md:flex flex-col">
        <div className="eyebrow">Amministrazione</div>
        <div className="font-serif text-2xl mt-1">Sarabi Beach</div>
        <nav className="mt-6 space-y-1">
          {NAV.filter((n) => hasPermission(user, n.perm)).map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm ${isActive ? "bg-bronze-600 text-white" : "hover:bg-sand-100"}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => cmdk.setOpen(true)} data-testid="open-command-palette"
                className="mt-3 flex items-center gap-2 rounded-xl border border-sand-300 bg-sand-50 hover:bg-sand-100 px-3 py-2 text-xs text-ink-500">
          <Command className="w-3 h-3" />
          <span className="flex-1 text-left">Cerca ovunque…</span>
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-sand-300 bg-white">⌘K</kbd>
        </button>
        <div className="mt-auto pt-4 border-t border-sand-300 text-xs text-ink-500">
          <div className="mb-2">{user?.name} · {user?.role}</div>
          <div className="flex flex-wrap gap-2">
            <a href="/preview-menu" target="_blank" rel="noopener noreferrer" className="btn-outline text-xs py-1 px-3" data-testid="admin-preview-menu"><Eye className="w-3 h-3 mr-1" />Vedi menu cliente</a>
            <Link to="/staff" className="btn-outline text-xs py-1 px-3" data-testid="admin-to-staff"><LayoutGrid className="w-3 h-3 mr-1" />Dashboard</Link>
            <Link to="/account/password" className="btn-outline text-xs py-1 px-3" data-testid="admin-change-password"><KeyRound className="w-3 h-3 mr-1" />Password</Link>
            <Button size="sm" variant="outline" onClick={() => { logout(); nav("/staff/login"); }} className="rounded-full" data-testid="admin-logout"><LogOut className="w-3 h-3" /></Button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <ConnectivityBanner />
        <MenuPublishBar canPublish={hasPermission(user, "menu.publish")} />
        <header className="md:hidden bg-white border-b border-sand-300 p-4 flex justify-between items-center overflow-x-auto gap-2">
          {NAV.filter((n) => hasPermission(user, n.perm)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `text-xs whitespace-nowrap rounded-full px-3 py-1.5 border ${isActive ? "bg-bronze-600 text-white border-bronze-600" : "border-sand-300"}`}>
              {n.label}
            </NavLink>
          ))}
        </header>
        <main className="p-6"><Outlet /></main>
      </div>
      <CommandPalette open={cmdk.open} onClose={() => cmdk.setOpen(false)} />
    </div>
  );
}
